/**
 * Navigation OPS — source unique des groupes et des entrées.
 *
 * Organisation PAR ESPACE (retour fondateur 05/10/2026 : « la sidebar est mal
 * organisée ») : chaque groupe correspond à un espace de travail, du plus
 * quotidien au plus transverse. Les `pageKey` sont inchangés : le RBAC
 * (`filterNavItems`) reste la seule autorité sur ce qu'un rôle voit, et un
 * groupe sans entrée visible est masqué.
 *
 * Le fichier est volontairement PUR (pas de hook, pas de JSX) : la sidebar le
 * consomme, les tests le lisent tel quel.
 */
import {
  LayoutDashboard,
  Users,
  Building2,
  Shield,
  FileText,
  FolderKanban,
  DollarSign,
  ClipboardList,
  Search,
  MessageSquare,
  MessageSquareWarning,
  Headset,
  BarChart3,
  Home,
  TrendingUp,
  BarChart2,
  CalendarDays,
  Rocket,
  UserCheck,
  Gift,
  AlertTriangle,
  Hammer,
  PhoneIncoming,
  Scale,
  Receipt,
  Timer,
  CreditCard,
  Bell,
  PiggyBank,
  FileSignature,
  RotateCcw,
  Filter,
  ShieldAlert,
  MapPin,
  Star,
  FileWarning,
  Radio,
  PhoneOutgoing,
  UserX,
  Tag,
  Flag,
  Copy,
  ZapOff,
  HandCoins,
  Layers,
  Send,
  Flame,
  Inbox,
  SearchCheck,
  Settings,
  Newspaper,
  Sparkles,
  Link2,
  CalendarRange,
  Library,
  FileStack,
  History,
  CalendarCheck,
  CalendarClock,
  KanbanSquare,
  Users2,
  Trophy,
  Wallet,
  Coins,
  SlidersHorizontal,
  Megaphone,
  Lightbulb,
  PenLine,
  Images,
  ClipboardCheck,
} from "lucide-react";
import type { NavItem, StaffRole } from "@/lib/rbac";
import { isFreelanceRole } from "@/lib/rbac";
import { MARKETPLACE_LABEL } from "@/lib/marketplaceBrand";

export interface NavGroup {
  key: string;
  label: string;
  items: NavItem[];
}

/** Compteurs affichés en badge, calculés par le shell (gatés par rôle). */
export interface NavBadges {
  goQueue?: number;
  unreadMessages?: number;
  support?: number;
  artisans?: number;
  slaUrgent?: number;
  trialAlerts?: number;
  fraudAlerts?: number;
  /** Espace freelance pro : notifications non lues du freelance connecté. */
  freelanceNotifications?: number;
}

export function buildNavGroups(role: StaffRole | undefined | null, badges: NavBadges = {}): NavGroup[] {
  const freelance = isFreelanceRole(role);

  return [
    // ═══════════ ACCUEIL ═══════════
    // Les deux cockpits : le dashboard PRINCIPAL agrège tous les business, le
    // dashboard marketplace (Bricko, clé technique TRAVAUXBTP) est la vue dédiée.
    {
      key: "accueil",
      label: "Accueil",
      items: [
        { path: "/", label: "Dashboard principal", icon: LayoutDashboard, pageKey: "DASHBOARD" },
        { path: "/travauxbtp", label: `Dashboard ${MARKETPLACE_LABEL}`, icon: Hammer, pageKey: "TRAVAUXBTP_DASHBOARD" },
        { path: "/search", label: "Recherche globale", icon: Search },
      ],
    },

    // ═══════════ ACQUISITION (sortante) ═══════════
    // Vivier SIRENE + séquences e-mail + appels : SUPER_ADMIN et ADMIN_OPS.
    {
      key: "acquisition",
      label: "Acquisition",
      items: [
        { path: "/acquisition-dashboard", label: "Vue d'ensemble", icon: BarChart3, pageKey: "ACQUISITION_DASHBOARD" },
        { path: "/acquisition-vivier", label: "Vivier BTP France", icon: Building2, pageKey: "ACQUISITION_VIVIER" },
        { path: "/acquisition-sequences", label: "Séquences", icon: Send, pageKey: "ACQUISITION_SEQUENCES" },
        { path: "/acquisition-warmup", label: "Warm-up e-mail", icon: Flame, pageKey: "ACQUISITION_WARMUP" },
        { path: "/acquisition-inbox", label: "Boîte de réception", icon: Inbox, pageKey: "ACQUISITION_INBOX" },
        { path: "/acquisition-calls", label: "Appels B2B", icon: PhoneOutgoing, pageKey: "ACQUISITION_CALLS" },
        { path: "/acquisition-copilot", label: "Copilote IA", icon: Sparkles, pageKey: "ACQUISITION_AI" },
        { path: "/acquisition-settings", label: "Réglages acquisition", icon: SlidersHorizontal, pageKey: "ACQUISITION_SETTINGS" },
      ],
    },

    // ═══════════ LEADS ENTRANTS ═══════════
    // Le haut de funnel inbound des deux produits, séparé de la prospection
    // sortante pour ne plus noyer 17 entrées dans un seul groupe.
    {
      key: "leads",
      label: "Leads entrants",
      items: [
        { path: "/lp-leads", label: "LP Leads", icon: PhoneIncoming, pageKey: "LP_LEADS" },
        { path: "/acquisition-funnel", label: "Funnel acquisition", icon: Filter, pageKey: "ACQUISITION_FUNNEL" },
        { path: "/private-projects", label: "Projets particuliers", icon: Home, pageKey: "PRIVATE_PROJECTS" },
        { path: "/ops-call-queue", label: "Call queue", icon: PhoneOutgoing, pageKey: "PRIVATE_PROJECTS" },
        { path: "/particulier-quality", label: "Qualité particuliers", icon: Star, pageKey: "PARTICULIER_QUALITY" },
        { path: "/conversion-pipeline", label: "Pipeline conversion", icon: TrendingUp, pageKey: "CONVERSION_PIPELINE" },
        { path: "/onboarding", label: "Onboarding", icon: Rocket, pageKey: "ONBOARDING" },
        { path: "/trial-alerts", label: "Alertes Trials", icon: Bell, badge: badges.trialAlerts, pageKey: "TRIAL_ALERTS" },
        { path: "/travauxbtp/referrals", label: "Parrainages", icon: Gift, pageKey: "TRAVAUXBTP_REFERRALS" },
        { path: "/broadcast", label: "Broadcast", icon: Radio, pageKey: "BROADCAST" },
      ],
    },

    // ═══════════ PROSPECTION FREELANCES (Lot A/B) ═══════════
    // Seul groupe visible des freelances (SDR / Sales) ; le RBAC masque chaque
    // entrée selon la population (SDR, Sales, manager).
    {
      key: "prospection",
      label: freelance ? "Espace prospection" : "Prospection freelances",
      items: [
        // Espace freelance pro (05/10/2026) : tableau de bord, dossier, versements, notifications.
        { path: "/prospection/tableau-de-bord", label: "Tableau de bord", icon: LayoutDashboard, pageKey: "PROSPECTION_DASHBOARD" },
        { path: "/prospection/appels", label: "Mes appels du jour", icon: PhoneOutgoing, pageKey: "PROSPECTION_CALLS" },
        { path: "/prospection/prospects", label: "Mes prospects", icon: Building2, pageKey: "PROSPECTION_COMPANIES" },
        { path: "/prospection/mes-rdv", label: "Mes RDV posés", icon: CalendarCheck, pageKey: "PROSPECTION_SDR_MEETINGS" },
        { path: "/prospection/stats-sdr", label: "Mes stats (SDR)", icon: BarChart2, pageKey: "PROSPECTION_SDR_STATS" },
        { path: "/prospection/rdv", label: "Mes RDV", icon: CalendarCheck, pageKey: "PROSPECTION_SALES_MEETINGS" },
        { path: "/prospection/disponibilites", label: "Mes disponibilités", icon: CalendarClock, pageKey: "PROSPECTION_SLOTS" },
        { path: "/prospection/pipeline", label: "Mon pipeline", icon: KanbanSquare, pageKey: "PROSPECTION_PIPELINE" },
        { path: "/prospection/stats", label: "Mes stats (Sales)", icon: BarChart3, pageKey: "PROSPECTION_SALES_STATS" },
        { path: "/prospection/conversions", label: "Mes conversions", icon: Trophy, pageKey: "PROSPECTION_CONVERSIONS" },
        { path: "/prospection/gains", label: "Mes gains", icon: Wallet, pageKey: "PROSPECTION_EARNINGS" },
        { path: "/prospection/versements", label: "Mes versements", icon: PiggyBank, pageKey: "PROSPECTION_PAYOUTS" },
        { path: "/prospection/notifications", label: "Notifications", icon: Bell, badge: badges.freelanceNotifications, pageKey: "PROSPECTION_NOTIFICATIONS" },
        { path: "/prospection/bienvenue", label: "Mon dossier", icon: FileSignature, pageKey: "PROSPECTION_ONBOARDING" },
        { path: "/prospection/pilotage", label: "Pilotage freelances", icon: Users2, pageKey: "PROSPECTION_MANAGE" },
        { path: "/prospection/commissions", label: "Commissions freelances", icon: Coins, pageKey: "PROSPECTION_COMMISSIONS" },
      ],
    },

    // ═══════════ CONTENU ═══════════
    // Module éditorial : articles et guides des deux sites, de l'idée à la
    // publication. Séparé du SEO Control Center (indexabilité des pages
    // métier×ville), qu'il ne touche jamais.
    {
      key: "contenu",
      label: "Contenu",
      items: [
        { path: "/contenu", label: "Dashboard contenu", icon: Newspaper, pageKey: "CONTENT" },
        { path: "/contenu/agent", label: "Agent rédacteur", icon: Sparkles, pageKey: "CONTENT" },
        { path: "/contenu/planning", label: "Planning éditorial", icon: CalendarRange, pageKey: "CONTENT" },
        { path: "/contenu/bibliotheque", label: "Bibliothèque", icon: Library, pageKey: "CONTENT" },
        { path: "/contenu/brouillons", label: "Brouillons", icon: FileStack, pageKey: "CONTENT" },
        { path: "/contenu/opportunites", label: "Opportunités SEO", icon: SearchCheck, pageKey: "CONTENT" },
        { path: "/contenu/historique", label: "Historique", icon: History, pageKey: "CONTENT" },
        { path: "/contenu/reglages", label: "Réglages éditoriaux", icon: Settings, pageKey: "CONTENT" },
        { path: "/netlinking", label: "Netlinking", icon: Link2, pageKey: "NETLINKING" },
      ],
    },

    // ═══════════ COMMUNICATION ═══════════
    // Réseaux sociaux (Bricko puis Constrigo) : idées, rédaction IA, visuels
    // de marque, validation humaine, calendrier. Aucune publication
    // automatique en lot 1.
    {
      key: "communication",
      label: "Communication",
      items: [
        { path: "/communication", label: "Calendrier", icon: Megaphone, pageKey: "COMMUNICATION" },
        { path: "/communication/idees", label: "Idées", icon: Lightbulb, pageKey: "COMMUNICATION" },
        { path: "/communication/atelier", label: "Atelier", icon: PenLine, pageKey: "COMMUNICATION" },
        { path: "/communication/visuels", label: "Visuels", icon: Images, pageKey: "COMMUNICATION" },
        { path: "/communication/a-valider", label: "À valider", icon: ClipboardCheck, pageKey: "COMMUNICATION" },
        { path: "/communication/statistiques", label: "Statistiques", icon: BarChart3, pageKey: "COMMUNICATION" },
        { path: "/communication/reglages", label: "Réglages communication", icon: Settings, pageKey: "COMMUNICATION" },
      ],
    },

    // ═══════════ SEO CONTROL CENTER ═══════════
    {
      key: "seo",
      label: "SEO Control Center",
      items: [
        { path: "/seo", label: "Vue d'ensemble SEO", icon: SearchCheck, pageKey: "SEO" },
        { path: "/seo/pages", label: "Pages", icon: FileText, pageKey: "SEO" },
        { path: "/seo/planner", label: "Planificateur de sorties", icon: CalendarDays, pageKey: "SEO" },
        { path: "/seo/issues", label: "Anomalies", icon: AlertTriangle, pageKey: "SEO" },
        { path: "/seo/settings", label: "Réglages SEO", icon: Settings, pageKey: "SEO" },
      ],
    },

    // ═══════════ COMMERCIAL ═══════════
    {
      key: "commercial",
      label: "Commercial",
      items: [
        { path: "/tenders", label: "Appels d'offres", icon: FileText, pageKey: "TENDERS" },
        { path: "/go-queue", label: "GO Queue", icon: ClipboardList, badge: badges.goQueue, pageKey: "GO_QUEUE" },
        { path: "/sales/pipeline", label: "Pipeline commercial", icon: TrendingUp, pageKey: "SALES_PIPELINE" },
        { path: "/sales/leads", label: "Leads", icon: Users, pageKey: "SALES_LEADS" },
        { path: "/sales/activities", label: "Activités", icon: CalendarDays, pageKey: "SALES_ACTIVITIES" },
        { path: "/sales/performance", label: "Performance", icon: BarChart2, pageKey: "SALES_PERFORMANCE" },
        { path: "/sales/forecast", label: "Prévisions", icon: BarChart3, pageKey: "SALES_FORECAST" },
        { path: "/companies", label: "Entreprises", icon: Building2, pageKey: "COMPANIES" },
        { path: "/subscriptions", label: "Abonnements", icon: CreditCard, pageKey: "SUBSCRIPTIONS" },
        { path: "/plans-saas", label: "Plans SaaS", icon: Layers, pageKey: "PLANS_SAAS" },
        { path: "/churn-risk", label: "Churn Risk", icon: AlertTriangle, pageKey: "CHURN_RISK" },
        { path: "/partners", label: "Partenaires", icon: Users, pageKey: "PARTNERS" },
      ],
    },

    // ═══════════ PRODUCTION & MODÉRATION ═══════════
    {
      key: "production",
      label: "Production et modération",
      items: [
        { path: "/travauxbtp/artisans", label: "Modération inscriptions", icon: UserCheck, badge: badges.artisans, pageKey: "ARTISANS_MODERATION" },
        { path: "/travauxbtp/artisans/all", label: "Annuaire artisans", icon: Users, pageKey: "ARTISANS_MODERATION" },
        { path: "/travauxbtp/documents", label: "Documents expiration", icon: FileWarning, pageKey: "DOCUMENTS_EXPIRATION" },
        { path: "/ops-inactive", label: "Artisans inactifs", icon: ZapOff, pageKey: "ARTISANS_MODERATION" },
        { path: "/ops-duplicates", label: "Comptes suspects", icon: Copy, pageKey: "ARTISANS_MODERATION" },
        { path: "/projects", label: "Projets clients SaaS", icon: FolderKanban, pageKey: "PROJECTS" },
        { path: "/sla-leads", label: "SLA Leads", icon: Timer, badge: badges.slaUrgent, pageKey: "SLA_LEADS" },
        { path: "/ops-no-shows", label: "No-shows artisans", icon: UserX, pageKey: "PRIVATE_PROJECTS" },
        { path: "/ops-quick-reports", label: "Signalements", icon: Flag, pageKey: "PRIVATE_PROJECTS" },
        { path: "/disputes", label: "Litiges", icon: Scale, pageKey: "DISPUTES" },
        { path: "/travauxbtp/reviews", label: "Modération avis", icon: Shield, pageKey: "REVIEWS_MODERATION" },
        { path: "/messagerie-moderation", label: "Modération messages", icon: MessageSquareWarning, pageKey: "MESSAGE_MODERATION" },
      ],
    },

    // ═══════════ FINANCE ═══════════
    {
      key: "finance",
      label: "Finance",
      items: [
        { path: "/transactions", label: "Transactions crédits", icon: Receipt, pageKey: "TRANSACTIONS" },
        { path: "/refunds", label: "Remboursements", icon: RotateCcw, pageKey: "REFUNDS" },
        { path: "/ops-bulk-gift", label: "Gift crédits (bulk)", icon: HandCoins, pageKey: "ARTISANS_MODERATION" },
        { path: "/billing", label: "Facturation interne", icon: DollarSign, pageKey: "BILLING" },
        { path: "/einvoicing", label: "Facturation électronique", icon: FileText, pageKey: "EINVOICING" },
      ],
    },

    // ═══════════ SUPPORT ═══════════
    {
      key: "support",
      label: "Support",
      items: [
        { path: "/messages", label: "Messagerie", icon: MessageSquare, badge: badges.unreadMessages, pageKey: "MESSAGES" },
        { path: "/support", label: "Tickets support", icon: Headset, badge: badges.support, pageKey: "SUPPORT" },
      ],
    },

    // ═══════════ ADMINISTRATION ═══════════
    {
      key: "administration",
      label: "Administration",
      items: [
        { path: "/fraud-alerts", label: "Alertes fraude", icon: ShieldAlert, badge: badges.fraudAlerts, pageKey: "FRAUD_ALERTS" },
        { path: "/ops-tags", label: "Tags VIP (artisans et particuliers)", icon: Tag, pageKey: "ARTISANS_MODERATION" },
        { path: "/audit-log", label: "Journal d'audit", icon: ClipboardList, pageKey: "AUDIT_LOG" },
        { path: "/marketplace-stats", label: "Stats Marketplace", icon: BarChart3, pageKey: "MARKETPLACE_STATS" },
        { path: "/marketplace-geo", label: "Densité géographique", icon: MapPin, pageKey: "MARKETPLACE_GEO" },
      ],
    },
  ];
}

/**
 * Entrée active = UNE seule : le chemin exact, sinon le plus long préfixe de
 * chemin (« /contenu/item/42 » active « /contenu », « /prospection/prospects/7 »
 * active « /prospection/prospects », jamais les deux). « / » n'est actif que
 * sur la racine exacte.
 */
export function resolveActivePath(pathname: string, groups: NavGroup[]): string | null {
  let best: string | null = null;
  for (const group of groups) {
    for (const item of group.items) {
      if (item.path === pathname) return item.path;
      if (item.path === "/") continue;
      if (pathname.startsWith(item.path + "/") && (best === null || item.path.length > best.length)) {
        best = item.path;
      }
    }
  }
  return best;
}
